/**
 * DispatcherChatDrawer - Chat drawer para Central de Operacoes
 * Permite comunicacao bidirecional com motoristas
 */

import {
 useState, useCallback, useEffect, useRef, useMemo 
} from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Badge,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Person as DriverIcon,
  SupportAgent as AgentIcon,
  Search as SearchIcon,
  CheckCircle as ReadIcon,
  Schedule as PendingIcon,
  Route as RouteIcon,
  KeyboardArrowLeft as BackIcon,
  Notifications as NotifyIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useEffectAsync } from '../../reactHelper';

// Formatar timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return 'Ontem';
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// Componente de mensagem
const MessageBubble = ({ message, isOwn, tokens }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: isOwn ? 'flex-end' : 'flex-start',
      mb: 1,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        maxWidth: '80%',
        borderRadius: 2,
        backgroundColor: isOwn ? tokens?.accent?.primary : tokens?.surface?.elevated,
        color: isOwn ? '#fff' : tokens?.text?.primary,
        borderTopRightRadius: isOwn ? 4 : 16,
        borderTopLeftRadius: isOwn ? 16 : 4,
      }}
    >
      <Typography sx={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
        {message.text}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
        <Typography sx={{ fontSize: '10px', opacity: 0.7 }}>
          {formatTime(message.timestamp)}
        </Typography>
        {isOwn && (
          message.read
            ? <ReadIcon sx={{ fontSize: 12 }} />
            : <PendingIcon sx={{ fontSize: 12 }} />
        )}
      </Box>
    </Paper>
  </Box>
);

// Mensagens rapidas para o dispatcher
const QUICK_REPLIES = [
  'Entendido, obrigado!',
  'Aguarde, vou verificar.',
  'Pode prosseguir.',
  'Entre em contato com o cliente.',
  'Retorne para a base.',
];

const DispatcherChatDrawer = ({
  open,
  onClose,
  drivers = [],
  routePlans = [],
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const messagesEndRef = useRef(null);

  // Estado
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0 = todos, 1 = com mensagens
  const [unreadCounts, setUnreadCounts] = useState({});

  // Filtrar motoristas
  const filteredDrivers = useMemo(() => {
    let result = drivers;

    // Filtro por texto
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter((d) => (d.name || '').toLowerCase().includes(search)
        || (d.phone || '').includes(search));
    }

    // Filtro por tab
    if (tabValue === 1) {
      result = result.filter((d) => unreadCounts[d.id] > 0);
    }

    return result;
  }, [drivers, searchText, tabValue, unreadCounts]);

  // Carregar contagem de nao lidos
  useEffectAsync(async () => {
    if (!open) return;

    try {
      const response = await fetch('/api/messages/unread-counts');
      if (response.ok) {
        const counts = await response.json();
        setUnreadCounts(counts);
      }
    } catch {
      // Mock data
      const mockCounts = {};
      drivers.forEach((d) => {
        mockCounts[d.id] = Math.random() > 0.7 ? Math.floor(Math.random() * 3) + 1 : 0;
      });
      setUnreadCounts(mockCounts);
    }
  }, [open, drivers]);

  // Carregar mensagens do motorista selecionado
  useEffectAsync(async () => {
    if (!selectedDriver) return;

    setLoading(true);
    try {
      // Mark messages as read
      fetch(`/api/messages/read?driverId=${selectedDriver.id}`, { method: 'PUT' }).catch(() => {});

      const response = await fetch(`/api/messages?driverId=${selectedDriver.id}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        // Mock data
        setMessages([
          {
            id: 1,
            text: 'Ola, tudo bem?',
            timestamp: new Date(Date.now() - 3600000).toISOString(),
            senderId: selectedDriver.id,
            read: true,
          },
          {
            id: 2,
            text: 'Ola! Como posso ajudar?',
            timestamp: new Date(Date.now() - 3500000).toISOString(),
            senderId: 'dispatcher',
            read: true,
          },
          {
            id: 3,
            text: 'Estou com duvida sobre a entrega na Av. Paulista',
            timestamp: new Date(Date.now() - 300000).toISOString(),
            senderId: selectedDriver.id,
            read: false,
          },
        ]);
      }
    } catch {
      // Ignore - using mock data
    } finally {
      setLoading(false);
    }
  }, [selectedDriver]);

  // Scroll para ultima mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Enviar mensagem
  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || !selectedDriver || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: messageText,
          recipientId: selectedDriver.id,
        }),
      });

      if (response.ok) {
        const sent = await response.json();
        setMessages((prev) => [...prev, sent]);
      } else {
        setMessages((prev) => [...prev, {
          id: Date.now(),
          text: messageText,
          timestamp: new Date().toISOString(),
          senderId: 'dispatcher',
          read: false,
        }]);
      }
    } catch {
      // Add locally on error
      setMessages((prev) => [...prev, {
        id: Date.now(),
        text: messageText,
        timestamp: new Date().toISOString(),
        senderId: 'dispatcher',
        read: false,
      }]);
    } finally {
      setSending(false);
    }
  }, [newMessage, selectedDriver, sending]);

  // Voltar para lista
  const handleBack = () => {
    setSelectedDriver(null);
    setMessages([]);
  };

  // Encontrar rota ativa do motorista
  const getDriverRoute = (driverId) => routePlans.find((plan) => plan.vehicles?.some((v) => v.driverId === driverId));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
          backgroundColor: tokens?.surface?.base,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: tokens?.surface?.raised,
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {selectedDriver ? (
          <>
            <IconButton size="small" onClick={handleBack}>
              <BackIcon />
            </IconButton>
            <Avatar sx={{ width: 36, height: 36, backgroundColor: tokens?.accent?.primary }}>
              <DriverIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                {selectedDriver.name}
              </Typography>
              {selectedDriver.phone && (
                <Typography sx={{ fontSize: '12px', color: tokens?.text?.tertiary }}>
                  {selectedDriver.phone}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <>
            <AgentIcon sx={{ color: tokens?.accent?.primary }} />
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '16px' }}>
              {t('chatTitle') || 'Mensagens'}
            </Typography>
          </>
        )}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {selectedDriver ? (
        /* Chat View */
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 64px)' }}>
          {/* Rota ativa */}
          {(() => {
            const route = getDriverRoute(selectedDriver.id);
            return route && (
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: tokens?.surface?.elevated,
                  borderBottom: `1px solid ${tokens?.border?.subtle}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <RouteIcon sx={{ fontSize: 16, color: tokens?.accent?.primary }} />
                <Typography sx={{ fontSize: '12px', color: tokens?.text?.secondary }}>
                  {route.name || `Rota #${route.id}`}
                </Typography>
                <Chip
                  size="small"
                  label={route.status}
                  sx={{ height: 20, fontSize: '10px' }}
                />
              </Box>
            );
          })()}

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isOwn={msg.senderType === 'dispatcher'}
                    tokens={tokens}
                  />
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          {/* Quick Replies */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${tokens?.border?.subtle}`,
              overflowX: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            {QUICK_REPLIES.map((reply, index) => (
              <Chip
                key={index}
                label={reply}
                size="small"
                onClick={() => setNewMessage(reply)}
                sx={{
                  mr: 0.5,
                  fontSize: '11px',
                  backgroundColor: tokens?.surface?.elevated,
                  '&:hover': {
                    backgroundColor: tokens?.accent?.subtle,
                  },
                }}
              />
            ))}
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, borderTop: `1px solid ${tokens?.border?.subtle}` }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Digite sua mensagem..."
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                      sx={{ color: newMessage.trim() ? tokens?.accent?.primary : tokens?.text?.tertiary }}
                    >
                      {sending ? <CircularProgress size={20} /> : <SendIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      ) : (
        /* Drivers List */
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 64px)' }}>
          {/* Search */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${tokens?.border?.subtle}` }}>
            <TextField
              fullWidth
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar motorista..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: tokens?.text?.tertiary, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{
              borderBottom: `1px solid ${tokens?.border?.subtle}`,
              '& .MuiTab-root': { textTransform: 'none', fontSize: '13px' },
            }}
          >
            <Tab label="Todos" />
            <Tab
              label={
                <Badge
                  badgeContent={Object.values(unreadCounts).reduce((a, b) => a + b, 0)}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16 } }}
                >
                  Nao lidas
                </Badge>
              }
            />
          </Tabs>

          {/* List */}
          <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
            {filteredDrivers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DriverIcon sx={{ fontSize: 40, color: tokens?.text?.tertiary }} />
                <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mt: 1 }}>
                  Nenhum motorista encontrado
                </Typography>
              </Box>
            ) : (
              filteredDrivers.map((driver) => {
                const route = getDriverRoute(driver.id);
                const unread = unreadCounts[driver.id] || 0;

                return (
                  <ListItem
                    key={driver.id}
                    button
                    onClick={() => setSelectedDriver(driver)}
                    sx={{
                      borderBottom: `1px solid ${tokens?.border?.subtle}`,
                      '&:hover': {
                        backgroundColor: tokens?.surface?.elevated,
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        badgeContent={unread}
                        color="error"
                        invisible={unread === 0}
                      >
                        <Avatar sx={{ backgroundColor: tokens?.accent?.primary }}>
                          <DriverIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={driver.name}
                      secondary={route ? `Rota: ${route.name || route.id}` : 'Sem rota ativa'}
                      primaryTypographyProps={{ fontWeight: unread > 0 ? 600 : 400, fontSize: '14px' }}
                      secondaryTypographyProps={{ fontSize: '12px' }}
                    />
                    {unread > 0 && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Mensagens nao lidas">
                          <NotifyIcon sx={{ color: tokens?.semantic?.warning, fontSize: 18 }} />
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>
      )}
    </Drawer>
  );
};

export default DispatcherChatDrawer;
